import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Alert,
    CircularProgress,
} from '@mui/material';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetTicketOptions } from '@openapi/ticket-options/ticket-options';

const ImprovedPriceMatrixEditor = ({ values, setFieldValue }) => {
    const { t } = useTranslation();
    const { priceMatrix, showId } = values;

    const {
        data: ticketOptionsData,
        isLoading: loading,
        error,
    } = useGetTicketOptions({
        optionalId: showId,
        page: 0,
        pageSize: 1000,
    });

    const ticketOptions = React.useMemo(
        () => ticketOptionsData?.ticketOptions || [],
        [ticketOptionsData],
    );

    const getCurrentPriceMatrix = useCallback(() => {
        const matrix = {};
        ticketOptions.forEach((option) => {
            const existingPrice = priceMatrix.find((p) => p.name === option.name);
            matrix[option.name] = existingPrice ? existingPrice.price : '';
        });
        return matrix;
    }, [priceMatrix, ticketOptions]);

    const updatePrice = useCallback(
        (optionName, price) => {
            const updatedMatrix = priceMatrix.filter((p) => p.name !== optionName);
            if (price !== '' && price !== null && price !== undefined) {
                updatedMatrix.push({
                    id: `${Math.random()}`.substring(2),
                    name: optionName,
                    price: price,
                });
            }
            setFieldValue('priceMatrix', updatedMatrix);
        },
        [priceMatrix, setFieldValue],
    );

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress />
            </div>
        );
    }

    if (error) {
        return <Alert severity="error">{t('tickettypes.errorLoadingTicketOptions')}</Alert>;
    }

    if (!ticketOptions || ticketOptions.length === 0) {
        return <Alert severity="info">{t('tickettypes.noTicketOptionsAvailable')}</Alert>;
    }

    const currentMatrix = getCurrentPriceMatrix();

    return (
        <div>
            <Alert severity="info" sx={{ mb: 2, '& .MuiAlert-message': { textAlign: 'left' } }}>
                {t('tickettypes.improvedPriceMatrixInfo')}
            </Alert>
            <TableContainer>
                <Table size="small" sx={{ minWidth: 360 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell width="50%">{t('tickettypes.ticketOption')}</TableCell>
                            <TableCell width="50%">{t('tickettypes.price')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {ticketOptions.map((option) => (
                        <TableRow key={option.id}>
                            <TableCell>
                                <strong>{option.name}</strong>
                                {option.description && (
                                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                                        {option.description}
                                    </div>
                                )}
                            </TableCell>
                            <TableCell>
                                <TextField
                                    fullWidth
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        step: 0.01,
                                        placeholder: t('tickettypes.noPrice'),
                                    }}
                                    value={
                                        currentMatrix[option.name] !== undefined
                                            ? currentMatrix[option.name]
                                            : ''
                                    }
                                    onChange={(ev) => {
                                        updatePrice(option.name, ev.target.value);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">$</InputAdornment>
                                        ),
                                    }}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default ImprovedPriceMatrixEditor;
